import React, { useState, useEffect } from "react";
import { Card, Button, Radio, Space, Typography, Row, Col, Spin, Badge, Modal, Collapse, message, Input, Popconfirm, Statistic } from "antd";
import { 
  EyeOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  LinkOutlined,
  SaveOutlined,
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  DownloadOutlined,
  HomeOutlined
} from "@ant-design/icons";
import hotelData from "../../hotel-data.json";
import { AdvertItem } from "./AdvertItem";

const { Title, Text, Link } = Typography;
const { Panel } = Collapse;

interface Advert {
  id: number;
  uid: number;
  title: string;
  description: string;
  shorttitle: string;
  summary: string;
  price: any;
  accommodation_type: number;
  no_persons: number;
  from_persons: number;
  category: string;
  status: number;
  owner: number;
  // Zusätzliche Felder aus der API
  slug?: string;
  city?: string;
  region?: number;
  star?: number;
}

interface GOPB {
  id: string;
  name: string;
  isActive: boolean;
  assignedAdverts: number[];
  generatedLink?: string;
}

export const GuidedOnepageBooking: React.FC = () => {
  const [gopbs, setGopbs] = useState<GOPB[]>([]);
  const [adverts, setAdverts] = useState<Advert[]>([]);
  const [loading, setLoading] = useState(true);
  const [jsonModalVisible, setJsonModalVisible] = useState(false);
  const [currentJsonData, setCurrentJsonData] = useState<any>(null);
  const [currentGopbId, setCurrentGopbId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  useEffect(() => {
    // TODO -> SELECT DB
    // Lade bestehende GOPBs aus der Datenbank
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      console.log('🔄 Loading GOPB data...');
      
      // Lade echte Adverts aus der API
      await loadAdverts();
      
      // Lade bestehende GOPBs aus der Datenbank
      await loadGopbs();
      
    } catch (error) {
      console.error('❌ Error loading GOPB data:', error);
      message.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const loadAdverts = async () => {
    try {
      const apiUrl = `${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=getAllMyAdverts`;
      
      const response = await fetch(apiUrl, {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📊 Received adverts data:', data);

      if (data && Array.isArray(data)) {
        setAdverts(data);
        console.log(`✅ Loaded ${data.length} adverts`);
      } else {
        console.warn('⚠️ No adverts data received');
        setAdverts([]);
      }
    } catch (error) {
      console.error('❌ Error loading adverts:', error);
      message.error('Fehler beim Laden der Unterkünfte');
      setAdverts([]);
    }
  };

  const loadGopbs = async () => {
    try {
      // TODO: Implementiere GOPB-API endpoint
      // Vorerst Mock-Daten mit echten Advert-IDs
      const mockGopbs: GOPB[] = [
        {
          id: "gopb-1",
          name: "Hauptbuchungsseite",
          isActive: true,
          assignedAdverts: [],
          generatedLink: "https://onepage.buchungsmanager.de?ASDKN123BKJBKNk1231kjnKJnk"
        },
        {
          id: "gopb-2",
          name: "Sommer-Special 2024",
          isActive: false,
          assignedAdverts: [],
        },
        {
          id: "gopb-3",
          name: "Business-Tarife",
          isActive: true,
          assignedAdverts: [],
          generatedLink: "https://onepage.buchungsmanager.de?BSDKN456CKJBKNk4561kjnKJnk"
        }
      ];

      setGopbs(mockGopbs);
      console.log('✅ Loaded GOPBs (mock data)');
    } catch (error) {
      console.error('❌ Error loading GOPBs:', error);
      setGopbs([]);
    }
  };

  const toggleGopbStatus = (gopbId: string) => {
    setGopbs(prevGopbs => 
      prevGopbs.map(gopb => {
        if (gopb.id === gopbId) {
          const newStatus = !gopb.isActive;
          return {
            ...gopb,
            isActive: newStatus,
            generatedLink: newStatus 
              ? `https://onepage.buchungsmanager.de?${btoa(gopb.id + Date.now())}`
              : undefined
          };
        }
        return gopb;
      })
    );
    message.success(`GOPB ${gopbs.find(g => g.id === gopbId)?.isActive ? 'deaktiviert' : 'aktiviert'}`);
  };

  const handleAdvertAssignment = (gopbId: string, advertId: number, assign: boolean) => {
    setGopbs(prevGopbs =>
      prevGopbs.map(gopb => {
        if (gopb.id === gopbId) {
          const newAdverts = assign
            ? [...gopb.assignedAdverts, advertId]
            : gopb.assignedAdverts.filter(id => id !== advertId);
          return {
            ...gopb,
            assignedAdverts: newAdverts
          };
        }
        return gopb;
      })
    );
  };

  const generateJsonData = (gopb: GOPB) => {
    const assignedRooms = adverts
      .filter(advert => gopb.assignedAdverts.includes(advert.uid))
      .map(advert => {
        // Extrahiere Preis aus dem Preis-JSON oder verwende 0 als Fallback
        let basePrice = 0;
        try {
          if (advert.price && typeof advert.price === 'string') {
            const priceData = JSON.parse(advert.price);
            basePrice = parseFloat(priceData.forTwoPersons || priceData.forOnePerson || 0);
          } else if (typeof advert.price === 'object' && advert.price) {
            basePrice = parseFloat(advert.price.forTwoPersons || advert.price.forOnePerson || 0);
          }
        } catch (e) {
          console.warn('Error parsing price for advert:', advert.uid, e);
        }

        // Finde die entsprechenden Raum-Daten aus hotel-data.json (falls vorhanden)
        const roomFromHotelData = hotelData.rooms.find((room: any) => room.id === advert.uid.toString());
        
        return roomFromHotelData || {
          id: advert.uid,
          name: { 
            de: advert.title || advert.shorttitle || 'Unbenannte Unterkunft', 
            en: advert.title || advert.shorttitle || 'Unnamed Accommodation' 
          },
          description: { 
            de: advert.summary || advert.description || '', 
            en: advert.summary || advert.description || '' 
          },
          basePrice: basePrice,
          images: [],
          capacity: { 
            adults: advert.no_persons || 2, 
            children: 0, 
            maxOccupancy: advert.no_persons || 2 
          },
          size: 25,
          features: { de: [], en: [] },
          amenities: [],
          rates: [],
          // Zusätzliche Advert-Informationen
          category: advert.category || '',
          city: advert.city || '',
          status: advert.status || 0,
          star: advert.star || 0
        };
      });

    return {
      hotel: hotelData.hotel,
      rooms: assignedRooms,
      extras: hotelData.extras,
      promoCodes: hotelData.promoCodes,
      policies: hotelData.policies,
      paymentMethods: hotelData.paymentMethods,
      generatedAt: new Date().toISOString(),
      gopbId: gopb.id,
      gopbName: gopb.name,
      totalRooms: assignedRooms.length
    };
  };

  const showJsonPreview = (gopb: GOPB) => {
    const jsonData = generateJsonData(gopb);
    setCurrentJsonData(jsonData);
    setCurrentGopbId(gopb.id);
    setJsonModalVisible(true);
  };

  const saveGopb = (gopb: GOPB) => {
    const jsonData = generateJsonData(gopb);
    
    // TODO -> INSERT DB
    // Hier wird die finale JSON für das jeweilige GOPB in die Datenbank gespeichert
    console.log("Speichere GOPB:", gopb.id, jsonData);
    
    message.success(`GOPB "${gopb.name}" wurde gespeichert`);
  };

  const copyLink = (link: string) => {
    navigator.clipboard.writeText(link);
    message.success("Link wurde in die Zwischenablage kopiert");
  };

  const startEditingName = (gopbId: string, currentName: string) => {
    setEditingId(gopbId);
    setEditingName(currentName);
  };

  const saveEditedName = (gopbId: string) => {
    if (editingName.trim()) {
      setGopbs(prevGopbs =>
        prevGopbs.map(gopb =>
          gopb.id === gopbId ? { ...gopb, name: editingName.trim() } : gopb
        )
      );
      message.success("Name wurde aktualisiert");
    }
    setEditingId(null);
    setEditingName("");
  };

  const deleteGopb = (gopbId: string) => {
    setGopbs(prevGopbs => prevGopbs.filter(gopb => gopb.id !== gopbId));
    message.success("GOPB wurde gelöscht");
  };

  const downloadJson = (gopb: GOPB) => {
    const jsonData = generateJsonData(gopb);
    const dataStr = JSON.stringify(jsonData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `gopb-${gopb.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    message.success(`JSON-Datei für "${gopb.name}" wurde heruntergeladen`);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const activeGopbs = gopbs.filter(g => g.isActive).length;
  const totalAdverts = adverts.length;
  const assignedAdverts = new Set(gopbs.flatMap(g => g.assignedAdverts)).size;

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>Guided OnePage Booking (GOPB) Verwaltung</Title>
          <Text type="secondary">
            Verwalten Sie Ihre One-Page-Booking-Seiten und weisen Sie Unterkünfte zu.
          </Text>
        </Col>
        <Col>
          <Space>
            <Button 
              icon={<DownloadOutlined />}
              onClick={() => {
                setLoading(true);
                loadInitialData();
              }}
            >
              Daten aktualisieren
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => {
                const newGopb: GOPB = {
                  id: `gopb-${Date.now()}`,
                  name: `Neue GOPB ${gopbs.length + 1}`,
                  isActive: false,
                  assignedAdverts: []
                };
                setGopbs([...gopbs, newGopb]);
                message.success("Neue GOPB wurde erstellt");
              }}
            >
              Neue GOPB erstellen
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Gesamt GOPBs" 
              value={gopbs.length} 
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Aktive GOPBs" 
              value={activeGopbs} 
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Verfügbare Unterkünfte" 
              value={totalAdverts} 
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Zugewiesene Unterkünfte" 
              value={assignedAdverts} 
              suffix={`/ ${totalAdverts}`}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {gopbs.map(gopb => (
          <Col span={24} key={gopb.id}>
            <Card
              title={
                <Space>
                  {editingId === gopb.id ? (
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onPressEnter={() => saveEditedName(gopb.id)}
                      onBlur={() => saveEditedName(gopb.id)}
                      style={{ width: 200 }}
                      autoFocus
                    />
                  ) : (
                    <>
                      {gopb.name}
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => startEditingName(gopb.id, gopb.name)}
                        style={{ marginLeft: 8 }}
                      />
                    </>
                  )}
                  <Badge 
                    status={gopb.isActive ? "success" : "default"} 
                    text={gopb.isActive ? "Aktiv" : "Inaktiv"} 
                  />
                </Space>
              }
              extra={
                <Space>
                  <Button
                    type={gopb.isActive ? "default" : "primary"}
                    icon={gopb.isActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                    onClick={() => toggleGopbStatus(gopb.id)}
                  >
                    {gopb.isActive ? "Deaktivieren" : "Aktivieren"}
                  </Button>
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => showJsonPreview(gopb)}
                  >
                    JSON-Vorschau
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => saveGopb(gopb)}
                  >
                    Speichern
                  </Button>
                  <Popconfirm
                    title="GOPB löschen?"
                    description="Sind Sie sicher, dass Sie diese GOPB löschen möchten?"
                    onConfirm={() => deleteGopb(gopb.id)}
                    okText="Ja, löschen"
                    cancelText="Abbrechen"
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                    >
                      Löschen
                    </Button>
                  </Popconfirm>
                </Space>
              }
            >
              {gopb.isActive && gopb.generatedLink && (
                <div style={{ marginBottom: 16, padding: 12, background: '#f0f2f5', borderRadius: 8 }}>
                  <Space>
                    <LinkOutlined />
                    <Text strong>Generierter Link:</Text>
                    <Link href={gopb.generatedLink} target="_blank">
                      {gopb.generatedLink}
                    </Link>
                    <Button 
                      size="small" 
                      icon={<CopyOutlined />}
                      onClick={() => copyLink(gopb.generatedLink!)}
                    >
                      Kopieren
                    </Button>
                  </Space>
                </div>
              )}

              <Collapse defaultActiveKey={['adverts']}>
                <Panel header={`Zugewiesene Unterkünfte (${gopb.assignedAdverts.length})`} key="adverts">
                  <div style={{ maxHeight: 400, overflowY: 'auto', paddingRight: 8 }}>
                    {adverts.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                        <HomeOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                        <br />
                        Keine Unterkünfte verfügbar. Bitte überprüfen Sie Ihre Berechtigung oder erstellen Sie zunächst Unterkünfte.
                      </div>
                    ) : (
                      adverts.map(advert => {
                      // Preis extrahieren
                      let basePrice = 0;
                      try {
                        if (advert.price && typeof advert.price === 'string') {
                          const priceData = JSON.parse(advert.price);
                          basePrice = parseFloat(priceData.forTwoPersons || priceData.forOnePerson || 0);
                        } else if (typeof advert.price === 'object' && advert.price) {
                          basePrice = parseFloat(advert.price.forTwoPersons || advert.price.forOnePerson || 0);
                        }
                      } catch (e) {
                        console.warn('Error parsing price for advert:', advert.uid, e);
                      }

                      // Legacy-Format für AdvertItem
                      const legacyAdvert = {
                        id: advert.uid.toString(),
                        name: advert.title || advert.shorttitle || 'Unbenannte Unterkunft',
                        description: advert.summary || advert.description || '',
                        roomType: advert.category || 'Unterkunft',
                        basePrice: basePrice
                      };

                      return (
                        <AdvertItem
                          key={advert.uid}
                          advert={legacyAdvert}
                          isAssigned={gopb.assignedAdverts.includes(advert.uid)}
                          onAssignmentChange={(assign) => handleAdvertAssignment(gopb.id, advert.uid, assign)}
                        />
                                             );
                     })
                     )}
                  </div>
                </Panel>
              </Collapse>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal
        title="JSON-Vorschau"
        open={jsonModalVisible}
        onCancel={() => setJsonModalVisible(false)}
        width={800}
        footer={[
          <Button 
            key="download" 
            icon={<DownloadOutlined />}
            onClick={() => {
              const gopb = gopbs.find(g => g.id === currentGopbId);
              if (gopb) downloadJson(gopb);
            }}
          >
            JSON herunterladen
          </Button>,
          <Button key="close" onClick={() => setJsonModalVisible(false)}>
            Schließen
          </Button>
        ]}
      >
        <pre style={{ 
          background: '#f5f5f5', 
          padding: 16, 
          borderRadius: 8, 
          overflow: 'auto',
          maxHeight: 500 
        }}>
          {JSON.stringify(currentJsonData, null, 2)}
        </pre>
      </Modal>
    </div>
  );
}; 